import { useEffect, useState } from "react";
import { Ban, Mic, MicOff, PhoneOff, X } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { showErrorFromMutation, showSuccess } from "@/shared/utils/toast";
import ConfirmModal from "@/shared/components/ui/Modal/ConfirmModal";
import { markDoNotCall } from "../api/voiceApi";
import { useCallStore } from "../stores/callStore";

const STATUS_LABELS = {
  connecting: "Connecting…",
  ringing: "Ringing…",
  in_call: "On call",
  ended: "Call ended",
  failed: "Call failed",
};

function formatElapsed(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
}

function useElapsed(since) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!since) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [since]);
  return since ? formatElapsed(now - since) : null;
}

const iconButton =
  "flex h-9 w-9 items-center justify-center rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary disabled:cursor-not-allowed disabled:opacity-50";

export default function ActiveCallBar() {
  const { status, callId, toNumber, contactName, answeredAt, muted, error } = useCallStore();
  const hangup = useCallStore((state) => state.hangup);
  const toggleMute = useCallStore((state) => state.toggleMute);
  const dismiss = useCallStore((state) => state.dismiss);
  const elapsed = useElapsed(status === "in_call" ? answeredAt : null);
  const [optedOut, setOptedOut] = useState(false);
  const [confirmingOptOut, setConfirmingOptOut] = useState(false);

  useEffect(() => {
    setOptedOut(false);
    setConfirmingOptOut(false);
  }, [callId]);

  const { mutate: doNotCall, isPending: isOptingOut } = useMutation({
    mutationFn: () => markDoNotCall(callId),
    onSuccess: () => {
      setOptedOut(true);
      showSuccess("This number will not be called again by anyone on SalesWorx.");
    },
    onError: (err) => showErrorFromMutation(err, "Could not add this number to the do-not-call list."),
  });

  if (status === "idle") return null;

  const isLive = ["connecting", "ringing", "in_call"].includes(status);
  const statusText = status === "in_call" && elapsed ? `${STATUS_LABELS.in_call} · ${elapsed}` : STATUS_LABELS[status];

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-5 left-1/2 z-[90] w-[min(440px,calc(100vw-32px))] -translate-x-1/2 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-lg dark:border-gray-700 dark:bg-gray-900"
    >
      <div className="flex items-center gap-3">
        <span
          className={`h-2.5 w-2.5 shrink-0 rounded-full ${
            status === "in_call" ? "bg-green-500" : status === "failed" ? "bg-red-500" : isLive ? "animate-pulse bg-amber-500" : "bg-gray-400"
          }`}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
            {contactName || toNumber}
          </p>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
            {contactName ? `${toNumber} · ` : ""}
            {statusText}
          </p>
        </div>

        {isLive ? (
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={toggleMute}
              disabled={status !== "in_call"}
              aria-pressed={muted}
              aria-label={muted ? "Unmute microphone" : "Mute microphone"}
              title={muted ? "Unmute" : "Mute"}
              className={`${iconButton} ${
                muted
                  ? "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
                  : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              {muted ? <MicOff size={16} /> : <Mic size={16} />}
            </button>
            <button
              type="button"
              onClick={hangup}
              aria-label="Hang up"
              title="Hang up"
              className={`${iconButton} border-red-600 bg-red-600 text-white hover:bg-red-700`}
            >
              <PhoneOff size={16} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close"
            title="Close"
            className={`${iconButton} border-transparent text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800`}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}

      {status !== "connecting" && callId && (
        <div className="mt-2 flex justify-end border-t border-gray-100 pt-2 dark:border-gray-800">
          <button
            type="button"
            onClick={() => setConfirmingOptOut(true)}
            disabled={optedOut || isOptingOut}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <Ban size={13} />
            {optedOut ? "On do-not-call list" : "Do not call again"}
          </button>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmingOptOut}
        destructive
        onClose={() => setConfirmingOptOut(false)}
        title="Do not call this number again?"
        description="It is added to the SalesWorx do-not-call list. Nobody on SalesWorx will be able to call it again."
        confirmText="Add to list"
        isLoading={isOptingOut}
        onConfirm={() => doNotCall(undefined, { onSettled: () => setConfirmingOptOut(false) })}
      />
    </div>
  );
}
